/* ------------------------------------------------------------------
   POWER.xyz 3D viewer — contrôleurs thème
   Doc SDK  : https://support.power.xyz/developers/3d-viewer/
   Anims    : https://support.power.xyz/developers/3d-viewer/animations/
   Hotspots : https://support.power.xyz/developers/3d-viewer/hotspots/
   Mouvement: https://repo.power.xyz/viewer-sdk/latest/movement.html

   Deux custom elements :
   - <power-viewer-stage>  : instancie POWERViewer, gère l'heure réelle,
                             expose une promesse `ready`.
   - <power-story>         : pilote les animations depuis des onglets
                             (mode "tabs") ou au scroll (mode "scroll"), et
                             en mode « plein cadre animé » chorégraphie la
                             montre — place à l'écran + cadrage caméra
                             interpolés en continu au scroll.
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  var SDK_TIMEOUT = 20000;
  var sdkPromise = null;

  /**
   * Diagnostic à la demande : taper `powerScene()` dans la console de
   * n'importe quelle page portant un viewer. Pas de drapeau à armer, pas de
   * paramètre d'URL à retenir — et il rend compte de l'état du viewer même
   * quand la scène n'est jamais arrivée, au lieu de laisser une console vide.
   */
  window.powerScene = function () {
    var stages = Array.prototype.slice.call(
      document.querySelectorAll('power-viewer-stage')
    );

    if (!stages.length) {
      console.warn('[power-viewer] aucun viewer sur cette page.');
      return;
    }

    stages.forEach(function (stage, index) {
      var id = stage.viewerId || '#' + index;

      console.group('[power-viewer] ' + id);
      console.info('SDK POWER chargé :', !!window.POWERViewer);
      console.info('contrôleur créé :', !!stage.viewer);
      console.info('modèle chargé :', stage.classList.contains('is-loaded'));
      console.info('scène reçue :', !!stage.sceneInfo);
      console.info('mise à l’heure demandée :', stage.dataset.watchTime === 'true');
      console.info('minuteur des aiguilles actif :', !!stage.watchTimer);
      console.info(
        'aiguilles introuvables :',
        stage.missingHands && stage.missingHands.length ? stage.missingHands.join(', ') : 'aucune'
      );
      console.info(
        'aiguilles trouvées par le viewer :',
        stage.rotatedNodes ? Object.keys(stage.rotatedNodes).join(', ') : 'aucune rotation acceptée'
      );

      // L'essentiel pour comprendre un cadrage qui n'arrive qu'au scroll :
      // le contrôle a-t-il été pris, et quand.
      var story = stage.closest ? stage.closest('power-story') : null;

      if (story) {
        console.info(
          'contrôle caméra pris :',
          story.controlAt
            ? new Date(story.controlAt).toLocaleTimeString() +
              (Date.now() < story.controlAt ? ' (à venir)' : ' (effectif)')
            : 'jamais — le chargement du modèle n’a pas été signalé'
        );
        console.info(
          'dernier cadrage envoyé :',
          story.appliedCamera
            ? Math.round(story.appliedCamera.azimuth) +
              '° · distance ' +
              Math.round(story.appliedCamera.dolly * 100) +
              ' %'
            : 'aucun'
        );
        console.info(
          'réaffirmation en cours :',
          !!(story.assertUntil && Date.now() < story.assertUntil)
        );
      }
      console.groupEnd();

      stage.inspect = true;

      if (stage.sceneInfo) {
        stage.reportScene(stage.sceneInfo);
        return;
      }

      console.info('[power-viewer] ' + id + ' : scène non reçue, nouvelle demande…');
      stage.sceneReceived = false;
      stage.watchdog = null;
      stage.requestSceneInfo();
    });
  };

  /**
   * Console : `powerHand('hand_seconds', 90)` fait pivoter un objet d'un
   * angle visible ; un troisième argument 'x' ou 'z' change d'axe. Sert à
   * trouver le nœud et l'axe auxquels le viewer répond sans avoir à déployer
   * entre deux essais.
   */
  window.powerHand = function (name, degrees, axis) {
    var stage = document.querySelector('power-viewer-stage');

    if (!stage || !stage.viewer) {
      console.warn('[power-viewer] aucun viewer prêt sur cette page.');
      return;
    }

    // Sans ça, le minuteur de mise à l'heure réécrit la rotation dans les
    // 125 ms : le test échouerait même avec le bon nom.
    stage.stopWatchTime();
    // Une piste active réécrirait la rotation à l'image suivante.
    stage.releaseAnimations();

    var rotation = { x: 0, y: 0, z: 0 };
    rotation[axis === 'x' || axis === 'z' ? axis : 'y'] = degrees || 0;

    stage.viewer.setObjectRotation(name, rotation);
    console.info(
      '[power-viewer] rotation ' +
        (degrees || 0) +
        '° sur ' +
        (axis === 'x' || axis === 'z' ? axis : 'y') +
        ' envoyée à « ' +
        name +
        ' ». Mise à l’heure suspendue — rechargez la page pour la relancer.'
    );
  };

  // Le fichier est chargé en différé et n'est injecté que là où un viewer est
  // rendu : cette ligne dit tout de suite s'il tourne vraiment sur la page.
  if (/[?&]power_debug/.test(window.location.search)) {
    console.info('[power-viewer] script chargé — tapez powerScene() pour le relevé.');
  }

  // Le SDK est chargé en <script type="module">, le global POWERViewer
  // n'est donc disponible qu'après évaluation du module.
  function whenSdkReady() {
    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise(function (resolve, reject) {
      if (window.POWERViewer) return resolve(window.POWERViewer);

      var started = Date.now();
      var timer = setInterval(function () {
        if (window.POWERViewer) {
          clearInterval(timer);
          resolve(window.POWERViewer);
        } else if (Date.now() - started > SDK_TIMEOUT) {
          clearInterval(timer);
          reject(new Error('SDK POWER.xyz indisponible (bloqué ou hors ligne).'));
        }
      }, 100);
    });

    return sdkPromise;
  }

  function splitList(value) {
    if (!value) return [];
    return value
      .split(',')
      .map(function (item) {
        return item.trim();
      })
      .filter(Boolean);
  }

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  function lerp(from, to, t) {
    return from + (to - from) * t;
  }

  // Le viewer raisonne en radians (démo officielle « scroll driven camera » :
  // demo.power.xyz/landing-page/simple). Les réglages de section sont en
  // degrés, lisibles par le marchand — la conversion se fait ici, au contact
  // du SDK, et nulle part ailleurs.
  /**
   * Parcourt la hiérarchie de scène renvoyée par onProductSceneInfo et
   * ramène tous les noms de nœuds. La forme exacte n'est pas documentée :
   * on accepte aussi bien un tableau d'enfants qu'un objet indexé par nom,
   * et on s'arrête net en cas de cycle.
   */
  function collectNodeNames(node, found, depth) {
    if (!node || depth > 12) return found;

    if (Array.isArray(node)) {
      node.forEach(function (child) {
        collectNodeNames(child, found, depth + 1);
      });
      return found;
    }

    if (typeof node !== 'object') return found;

    if (typeof node.name === 'string' && node.name) found.push(node.name);

    var children = node.children || node.nodes || node.childs;

    if (children && typeof children === 'object') {
      if (Array.isArray(children)) {
        children.forEach(function (child) {
          collectNodeNames(child, found, depth + 1);
        });
      } else {
        Object.keys(children).forEach(function (key) {
          if (typeof children[key] === 'object' && !children[key].name) found.push(key);
          collectNodeNames(children[key], found, depth + 1);
        });
      }
    }

    return found;
  }

  function sceneNodeNames(info) {
    if (!info) return [];

    var names = collectNodeNames(info.hierarchy || info.scene || info, [], 0);

    return names.filter(function (name, index) {
      return names.indexOf(name) === index;
    });
  }

  function sceneAnimationNames(info) {
    if (!info || !info.animations) return [];
    if (Array.isArray(info.animations)) return info.animations.slice();
    return Object.keys(info.animations);
  }

  // Aiguilles : on cherche large, le modèle peut être nommé en anglais comme
  // en français, avec ou sans séparateur.
  var HAND_HINT = /(hand|aiguille|needle|seconde?s?|minute|hour|heure)/i;

  // Durée du fondu de reprise de la caméra après l'animation d'accueil.
  var TAKEOVER_MS = 1000;

  // Fenêtre pendant laquelle on réaffirme notre cadrage image par image après
  // le chargement. L'animation d'accueil du viewer est pilotée de son côté :
  // poser la caméra une seule fois ne suffit pas, elle la reprend juste après.
  var ASSERT_MS = 2500;

  var DEG_TO_RAD = Math.PI / 180;
  var RAD_TO_DEG = 180 / Math.PI;

  // Même easing que la démo POWER : quintique, plus lente à démarrer et à
  // s'arrêter qu'un smoothstep, c'est ce qui fait « plan de film ».
  function easeInOut(t) {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /**
   * Lit la chorégraphie posée sur une étape : où la montre se place à l'écran
   * (x de 0 = bord gauche à 1 = bord droit, y en % de la hauteur du cadre) et
   * quel cadrage caméra elle adopte.
   */
  function readKeyframe(beat) {
    var frame = {
      x: clamp(toNumber(beat.dataset.align, 1), 0, 1),
      y: toNumber(beat.dataset.offsetY, 0),
      camera: null
    };

    if (beat.dataset.camera === 'true') {
      frame.camera = {
        azimuth: toNumber(beat.dataset.camAzimuth, 0),
        polar: toNumber(beat.dataset.camPolar, 0),
        dolly: toNumber(beat.dataset.camDolly, 3.5),
        targetX: toNumber(beat.dataset.camTargetX, 0),
        targetY: toNumber(beat.dataset.camTargetY, 0),
        targetZ: toNumber(beat.dataset.camTargetZ, 0)
      };
    }

    return frame;
  }

  // Une étape sans cadrage propre conserve celui de l'étape précédente : la
  // caméra ne doit pas retomber brutalement sur (0, 0, 1) entre deux plans.
  function fillCameras(frames) {
    var previous = null;

    frames.forEach(function (frame) {
      if (frame.camera) previous = frame.camera;
      else frame.camera = previous;
    });

    previous = null;

    for (var i = frames.length - 1; i >= 0; i--) {
      if (frames[i].camera) previous = frames[i].camera;
      else frames[i].camera = previous;
    }
  }

  // Le relevé caméra arrive en radians ; nos étapes raisonnent en degrés.
  function orbitToCamera(orbit) {
    if (!orbit || typeof orbit.azimuth !== 'number') return null;

    var target = orbit.target || {};

    return {
      azimuth: orbit.azimuth * RAD_TO_DEG,
      polar: (orbit.polar || 0) * RAD_TO_DEG,
      dolly: typeof orbit.dolly === 'number' ? orbit.dolly : 1,
      targetX: target.x || 0,
      targetY: target.y || 0,
      targetZ: target.z || 0
    };
  }

  function mixCameras(from, to, t) {
    if (!from) return to;
    if (!to) return from;

    return {
      // Linéaire, et non par le plus court chemin : c'est la seule façon
      // d'exprimer un tour complet (180 puis 360) ou de choisir son sens.
      azimuth: lerp(from.azimuth, to.azimuth, t),
      polar: lerp(from.polar, to.polar, t),
      dolly: lerp(from.dolly, to.dolly, t),
      targetX: lerp(from.targetX, to.targetX, t),
      targetY: lerp(from.targetY, to.targetY, t),
      targetZ: lerp(from.targetZ, to.targetZ, t)
    };
  }

  function sameCamera(a, b) {
    if (!a || !b) return false;

    return (
      Math.abs(a.azimuth - b.azimuth) < 0.05 &&
      Math.abs(a.polar - b.polar) < 0.05 &&
      Math.abs(a.dolly - b.dolly) < 0.002 &&
      a.targetX === b.targetX &&
      a.targetY === b.targetY &&
      a.targetZ === b.targetZ
    );
  }

  /**
   * Relevé caméra : onOrbitUpdate diffuse des radians et un dolly brut. On
   * les affiche dans l'unité des réglages d'étape — degrés, et distance en
   * centièmes — pour que le marchand recopie sans convertir.
   */
  function formatOrbit(data) {
    if (!data || typeof data !== 'object') return String(data);

    var angles = ['azimuth', 'polar'];
    var parts = [];

    angles.forEach(function (key) {
      if (typeof data[key] !== 'number') return;
      parts.push(key + ' ' + Math.round(data[key] * RAD_TO_DEG) + '°');
    });

    if (typeof data.dolly === 'number') {
      parts.push('distance ' + Math.round(data.dolly * 100) + ' %');
    }

    if (typeof data.target === 'object' && data.target) {
      parts.push(
        'cible ' +
          [data.target.x, data.target.y, data.target.z]
            .map(function (value) {
              return typeof value === 'number' ? value.toFixed(2) : '0';
            })
            .join(', ')
      );
    }

    // Charge utile non documentée : si elle ne porte pas les clés attendues,
    // on affiche ce qu'elle contient vraiment plutôt que rien.
    if (!parts.length) return JSON.stringify(data);

    return parts.join('  ·  ');
  }

  /* ---------------------------------------------------------------- */
  /* <power-viewer-stage>                                             */
  /* ---------------------------------------------------------------- */

  class PowerViewerStage extends HTMLElement {
    connectedCallback() {
      // Swiper 10 en mode loop déplace physiquement les slides dans le DOM
      // (pas de clonage) : le custom element est déconnecté puis reconnecté.
      // On ne réinstancie pas le viewer, on relance juste l'observation.
      if (this.initialised) {
        this.observeVisibility();
        this.syncWatchTimer();
        return;
      }

      this.initialised = true;

      // design_mode ne vaut que dans l'iframe de l'éditeur : sur la boutique,
      // ?power_debug=1 donne le même relevé sans avoir à ouvrir l'éditeur.
      this.inspect =
        this.dataset.inspect === 'true' ||
        /[?&]power_debug/.test(window.location.search);

      this.viewerElement = this.querySelector('power-viewer');
      this.viewerId = this.viewerElement ? this.viewerElement.id : null;
      this.watchTimer = null;
      this.isVisible = true;

      if (!this.viewerId) {
        console.warn('[power-viewer] <power-viewer id="..."> manquant.');
        this.ready = Promise.resolve(null);
        return;
      }

      this.ready = whenSdkReady()
        .then(
          function (POWERViewer) {
            this.viewer = new POWERViewer({ id: this.viewerId });

            if (typeof this.viewer.onLoad === 'function') {
              this.viewer.onLoad(
                function () {
                  this.requestSceneInfo();
                  this.markLoaded();
                }.bind(this)
              );
            } else {
              this.markLoaded();
            }

            this.requestSceneInfo();

            if (typeof this.viewer.onOrbitUpdate === 'function') {
              this.viewer.onOrbitUpdate(
                function (orbit) {
                  this.lastOrbit = orbit;
                }.bind(this)
              );
            }

            if (typeof this.viewer.onProductSceneInfo === 'function') {
              this.viewer.onProductSceneInfo(
                function (info) {
                  this.sceneInfo = info;
                  this.dispatchEvent(
                    new CustomEvent('power:sceneinfo', { bubbles: true, detail: info })
                  );
                  this.sceneReceived = true;
                  this.checkHands(info);
                  // La scène ne peut arriver que d'un modèle chargé : c'est
                  // notre filet quand le message « loaded » est passé avant
                  // que le contrôleur n'existe.
                  this.markLoaded();
                  if (this.inspect) this.reportScene(info);
                }.bind(this)
              );
            }

            // Seul accusé de réception de setObjectRotation : le viewer ne
            // répond que s'il a trouvé le nœud visé.
            if (typeof this.viewer.onManualUpdate === 'function') {
              this.viewer.onManualUpdate(
                function (data) {
                  if (!data || data.type !== 'object_rotation' || !data.obj) return;
                  this.rotatedNodes = this.rotatedNodes || {};
                  this.rotatedNodes[data.obj] = true;
                }.bind(this)
              );
            }

            if (typeof this.viewer.onAnimationFinished === 'function') {
              this.viewer.onAnimationFinished(
                function (data) {
                  // Une piste rembobinée est revenue à sa pose de repos : la
                  // garder active ne sert plus qu'à figer les aiguilles.
                  if (data && data.timeScale < 0) this.releaseAnimations();
                  // Hors power-story, rien dans le thème ne lance de piste :
                  // une piste qui finit est l'accueil POWER, et elle
                  // retiendrait les aiguilles indéfiniment.
                  else if (
                    data &&
                    this.dataset.watchTime === 'true' &&
                    !this.closest('power-story') &&
                    sceneAnimationNames(this.sceneInfo).indexOf(data.animationName) !== -1
                  ) {
                    this.releaseAnimations();
                  }
                  this.dispatchEvent(
                    new CustomEvent('power:animationfinished', { bubbles: true, detail: data })
                  );
                }.bind(this)
              );
            }

            return this.viewer;
          }.bind(this)
        )
        .catch(
          function (error) {
            console.warn('[power-viewer]', error.message);
            this.classList.add('has-error');
            return null;
          }.bind(this)
        );

      this.observeVisibility();
    }

    disconnectedCallback() {
      this.stopWatchTime();
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onDocumentVisibility);
    }

    /**
     * Le SDK ne diffuse « loaded » qu'une fois, sans le rejouer pour un
     * abonné tardif : un contrôleur instancié après le chargement du modèle
     * ne l'entend jamais. Le poster de repli resterait alors par-dessus la 3D
     * et la mise à l'heure ne démarrerait pas. On accepte donc deux signaux —
     * onLoad, et l'arrivée de la scène — et on n'en retient que le premier.
     */
    markLoaded() {
      if (this.isLoaded) return;
      this.isLoaded = true;

      this.classList.add('is-loaded');
      this.dispatchEvent(new CustomEvent('power:load', { bubbles: true }));
      this.syncWatchTimer();
    }

    // On ne fait tourner les aiguilles que quand le viewer est réellement
    // à l'écran : setObjectRotation tourne à 8 appels/seconde.
    // Idempotent : appelé à chaque reconnexion provoquée par Swiper.
    observeVisibility() {
      if (!this.onDocumentVisibility) {
        this.onDocumentVisibility = function () {
          this.syncWatchTimer();
        }.bind(this);
      }

      document.removeEventListener('visibilitychange', this.onDocumentVisibility);
      document.addEventListener('visibilitychange', this.onDocumentVisibility);

      if (!('IntersectionObserver' in window)) return;

      if (!this.observer) {
        this.observer = new IntersectionObserver(
          function (entries) {
            this.isVisible = entries[0].isIntersecting;
            this.syncWatchTimer();
          }.bind(this),
          { rootMargin: '200px' }
        );
      }

      this.observer.observe(this);
    }

    syncWatchTimer() {
      if (this.dataset.watchTime !== 'true' || !this.viewer) return;

      var shouldRun = this.isVisible && document.visibilityState !== 'hidden';

      if (shouldRun && !this.watchTimer) this.startWatchTime();
      if (!shouldRun && this.watchTimer) this.stopWatchTime();
    }

    /**
     * Met la montre à l'heure du visiteur.
     * Formules et cadence (125 ms) reprises de la démo officielle POWER
     * (repo.power.xyz/viewer-sdk/latest/movement.html).
     */
    /**
     * onProductSceneInfo ne reçoit rien tant que la scène n'a pas été
     * demandée : les composants du SDK envoient ce message juste avant de
     * s'abonner. Pas de méthode publique pour ça, d'où l'appel direct — gardé
     * par un typeof, le jour où le SDK le renomme on perd le diagnostic, pas
     * le viewer.
     */
    requestSceneInfo() {
      if (!this.viewer || typeof this.viewer._sendViewerMessage !== 'function') return;
      if (this.sceneReceived) return;

      this.viewer._sendViewerMessage({
        type: 'METAVRS.viewer.sendConfig',
        viewerId: this.viewer.id,
        value: null
      });

      if (!this.inspect || this.watchdog) return;

      // Sans ce garde-fou, un SDK qui ne répondrait pas laisserait une console
      // vide — indiscernable d'un modèle sans défaut.
      this.watchdog = setTimeout(
        function () {
          if (this.sceneReceived) return;
          console.warn(
            '[power-viewer] aucune information de scène reçue : ' +
              "le viewer n'a pas répondu à la demande, ou la version du SDK " +
              'ne la diffuse pas. Les noms de mesh ne peuvent pas être vérifiés.'
          );
        }.bind(this),
        4000
      );
    }

    /**
     * Le viewer fait tourner son mixer three.js à chaque image, et une piste
     * reste active tant qu'on ne l'arrête pas — y compris en pause (via
     * setProductAnimationPercent) ou retenue sur sa dernière image (hold).
     * Active, elle réécrit la rotation de tous les nœuds qu'elle anime juste
     * après notre setObjectRotation : si les aiguilles en font partie, elles
     * ne bougent plus. Aucune méthode publique pour l'arrêter ; le viewer
     * accepte pourtant ce message, qui arrête tout et rend aux nœuds leur
     * pose d'origine.
     */
    releaseAnimations() {
      if (!this.viewer || typeof this.viewer._sendViewerMessage !== 'function') return;

      this.viewer._sendViewerMessage({
        type: 'METAVRS.viewer.resetAnimation',
        viewerId: this.viewer.id,
        value: null
      });
    }

    handNames() {
      return {
        seconds: splitList(this.dataset.handSeconds || 'hand_seconds'),
        minutes: splitList(this.dataset.handMinutes || 'hand_minutes'),
        hours: splitList(this.dataset.handHours || 'hand_hours')
      };
    }

    /**
     * setObjectRotation n'échoue jamais bruyamment : viser un mesh absent
     * revient à pousser huit messages par seconde dans le vide. Dès que la
     * scène est connue, on vérifie que les aiguilles existent vraiment.
     */
    checkHands(info) {
      if (this.dataset.watchTime !== 'true') return;

      var nodes = sceneNodeNames(info);
      if (!nodes.length) return;

      var wanted = this.handNames();

      // Une aiguille ne manque que si AUCUN de ses mesh n'est dans la scène :
      // viser en plus un nom absent est sans effet, pas une erreur.
      this.missingHands = Object.keys(wanted).filter(function (key) {
        return !wanted[key].some(function (name) {
          return nodes.indexOf(name) !== -1;
        });
      });

      if (this.missingHands.length) this.stopWatchTime();
    }

    startWatchTime() {
      if (!this.viewer || typeof this.viewer.setObjectRotation !== 'function') return;
      if (this.missingHands && this.missingHands.length) return;

      this.stopWatchTime();

      var hands = this.handNames();
      var viewer = this.viewer;

      // L'axe dépend de la pose du modèle : la démo POWER est à plat (y), la
      // montre AM2 est debout face caméra (z). Sur le mauvais axe, l'aiguille
      // pivote sur sa longueur et paraît figée. « -z » inverse le sens.
      var axisSetting = (this.dataset.handAxis || 'z').trim();
      var sign = axisSetting.charAt(0) === '-' ? -1 : 1;
      var axis = axisSetting.replace('-', '');
      if (axis !== 'x' && axis !== 'y') axis = 'z';

      var turn = function (names, degrees) {
        var rotation = { x: 0, y: 0, z: 0 };
        rotation[axis] = degrees * sign;

        names.forEach(function (name) {
          viewer.setObjectRotation(name, rotation);
        });
      };

      var tick = function () {
        var d = new Date();
        var s = d.getSeconds() * 1000 + d.getMilliseconds();
        var midnight = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
        var diff = (d - midnight) / 1000;

        turn(hands.seconds, -(s * 360) / 60000);
        turn(hands.minutes, -(diff * ((360 * 24) / 60)) / 1440);
        turn(hands.hours, -(diff * 720) / 86400);
      };

      tick();
      this.watchTimer = setInterval(tick, 125);
    }

    stopWatchTime() {
      if (!this.watchTimer) return;
      clearInterval(this.watchTimer);
      this.watchTimer = null;
    }

    /**
     * Éditeur de thème uniquement. setObjectRotation et playProductAnimation
     * ne renvoient rien et échouent en silence quand le nom visé n'existe pas
     * dans le modèle : sans ce rapport, une aiguille mal nommée est
     * indiscernable d'une aiguille non gérée par POWER.
     */
    reportScene(info) {
      var nodes = sceneNodeNames(info);
      var animations = sceneAnimationNames(info);

      console.group(
        '[power-viewer] modèle chargé — ' +
          nodes.length +
          ' nœuds, ' +
          animations.length +
          ' animation(s)'
      );
      if (animations.length) console.info('Animations :', animations.join(', '));
      console.info('Nœuds :', nodes.join(', '));
      console.info('Réponse brute du SDK :', info);
      console.groupEnd();

      if (this.dataset.watchTime !== 'true') return;
      if (!this.missingHands || !this.missingHands.length) return;

      var labels = {
        hours: 'aiguille des heures',
        minutes: 'aiguille des minutes',
        seconds: 'aiguille des secondes'
      };
      var wanted = this.handNames();
      var missing = this.missingHands;

      var candidates = nodes.filter(function (name) {
        return HAND_HINT.test(name);
      });

      var message =
        "Mise à l'heure inactive : " +
        missing
          .map(function (key) {
            return labels[key] + ' « ' + wanted[key].join(', ') + ' »';
          })
          .join(', ') +
        (missing.length > 1 ? ' sont introuvables' : ' est introuvable') +
        ' dans le modèle.';

      var hint = candidates.length
        ? 'Noms approchants trouvés : ' + candidates.join(', ') + '.'
        : "Aucun nœud ne ressemble à une aiguille : le modèle n'expose sans doute pas d'aiguilles nommées — à confirmer avec POWER.";

      console.warn('[power-viewer] ' + message + ' ' + hint);
      this.showDiagnostic(message + ' ' + hint);
    }

    showDiagnostic(text) {
      var host = this.parentElement;
      if (!host) return;

      var panel = host.querySelector('[data-power-diagnostic]');

      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'power-viewer__diagnostic';
        panel.setAttribute('data-power-diagnostic', '');
        host.appendChild(panel);
      }

      panel.textContent = text;
      panel.hidden = false;
    }
  }

  if (!customElements.get('power-viewer-stage')) {
    customElements.define('power-viewer-stage', PowerViewerStage);
  }

  /* ---------------------------------------------------------------- */
  /* <power-story>                                                    */
  /* ---------------------------------------------------------------- */

  class PowerStory extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.mode = this.dataset.mode === 'scroll' ? 'scroll' : 'tabs';
      this.stage = this.querySelector('power-viewer-stage');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-power-tab]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-power-panel]'));
      this.beats = Array.prototype.slice.call(this.querySelectorAll('[data-power-beat]'));
      this.indicatorCurrent = this.querySelector('[data-power-current]');
      this.progressFill = this.querySelector('[data-power-progress-fill]');
      this.activeIndex = null;
      this.currentAnimation = null;

      // Chorégraphie : chaque étape porte une place à l'écran et un cadrage
      // caméra ; en mode « plein cadre animé » le scroll interpole de l'une à
      // l'autre au lieu de sauter d'une étape à la suivante.
      this.cinematic = this.classList.contains('power-story--cinematic');
      this.keyframes = this.beats.map(readKeyframe);
      fillCameras(this.keyframes);
      this.hasCamera = this.keyframes.some(function (frame) {
        return !!frame.camera;
      });
      this.cameraDuration = toNumber(this.dataset.cameraDuration, 1.2);
      // Part du retard rattrapée à chaque frame : plus c'est bas, plus la
      // montre traîne derrière le scroll.
      this.smoothing = clamp(toNumber(this.dataset.scrollSmoothing, 0.12), 0.02, 1);
      // En portrait le cadre est étroit : à distance égale le modèle déborde.
      // La démo POWER recule la caméra de la même façon (mobile.dollyOffset).
      this.mobileDolly = toNumber(this.dataset.mobileDolly, 0.5);
      // Le viewer joue sa propre animation d'accueil au chargement. Lui
      // reprendre la caméra pendant ce temps fait se superposer deux
      // pilotages, d'où les artefacts. On attend, puis on reprend en fondu.
      this.takeoverDelay = toNumber(this.dataset.takeoverDelay, 0);
      this.controlAt = null;
      this.takeoverStart = null;
      this.takeoverFrom = null;
      // Sans attente, pas de fondu : le fondu serait lui-même une animation
      // d'accueil, alors qu'on veut le modèle déjà cadré dès son apparition.
      this.takeoverDone = !this.takeoverDelay;
      this.assertUntil = 0;

      this.addEventListener(
        'power:load',
        function () {
          this.controlAt = Date.now() + this.takeoverDelay;
          this.assertUntil = Date.now() + this.takeoverDelay + ASSERT_MS;
          this.runLoop();
          this.freezeIntro();
          setTimeout(this.freezeIntro.bind(this, null), 600);
        }.bind(this)
      );
      this.smoothProgress = 0;
      this.appliedCamera = null;
      this.frameRequest = null;

      this.tabs.forEach(
        function (tab) {
          tab.addEventListener(
            'click',
            function () {
              this.activate(parseInt(tab.dataset.powerTab, 10), true);
            }.bind(this)
          );
        }.bind(this)
      );

      // En mode scroll, la barre d'onglets sert de repli sous 990px : le
      // scroll pilote au-dessus, le clic en dessous. Le point de rupture est
      // celui du thème.
      this.mobileTabs = this.classList.contains('power-story--mobile-tabs');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onBreakpointChange = this.syncBehaviour.bind(this);

      if (this.desktopQuery.addEventListener) {
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);
        this.motionQuery.addEventListener('change', this.onBreakpointChange);
      } else if (this.desktopQuery.addListener) {
        this.desktopQuery.addListener(this.onBreakpointChange);
        this.motionQuery.addListener(this.onBreakpointChange);
      }

      // Le stage diffuse la scène dès qu'il l'obtient : le modèle est alors
      // chargé, l'accueil a pu démarrer.
      this.addEventListener('power:sceneinfo', this.freezeIntro.bind(this));
      this.addEventListener(
        'power:animationfinished',
        function (event) {
          this.releaseForeignAnimation(event.detail);
        }.bind(this)
      );

      this.setupXray();
      this.setupHud();
      this.syncBehaviour();
    }

    disconnectedCallback() {
      this.unobserveBeats();
      this.stopScrub();

      if (!this.desktopQuery) return;

      if (this.desktopQuery.removeEventListener) {
        this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
        this.motionQuery.removeEventListener('change', this.onBreakpointChange);
      } else if (this.desktopQuery.removeListener) {
        this.desktopQuery.removeListener(this.onBreakpointChange);
        this.motionQuery.removeListener(this.onBreakpointChange);
      }
    }

    syncBehaviour() {
      this.scrollDriven =
        this.mode === 'scroll' && (this.desktopQuery.matches || !this.mobileTabs);

      // Le mouvement continu suit le pilotage au scroll, mobile compris : sur
      // écran étroit la montre ne traverse plus (pas de place), mais elle
      // continue de tourner, de zoomer et de descendre. Seuls le repli en
      // onglets et le refus d'animation le coupent.
      this.scrubbing = this.cinematic && this.scrollDriven && !this.motionQuery.matches;

      // Le recul mobile change avec le point de rupture : sans ça, le dernier
      // cadrage appliqué serait considéré comme déjà bon après rotation.
      this.appliedCamera = null;

      if (this.scrollDriven) {
        this.observeBeats();
      } else {
        this.unobserveBeats();
      }

      if (this.scrubbing) {
        this.startScrub();
      } else {
        this.stopScrub();
      }

      // Toujours au moins une étape active, quel que soit le pilotage.
      if (this.activeIndex === null && this.beats.length) {
        this.activate(0, false);
      } else if (!this.scrubbing && this.beats.length) {
        this.applyKeyframe(this.keyframes[this.activeIndex || 0], false);
      }
    }

    /* -- viewer ---------------------------------------------------------- */

    withViewer(callback) {
      if (!this.stage || !this.stage.ready) return;

      if (this.viewer) {
        callback(this.viewer);
        return;
      }

      this.stage.ready.then(
        function (viewer) {
          if (!viewer) return;
          this.viewer = viewer;
          callback(viewer);
        }.bind(this)
      );
    }

    /* -- étapes ---------------------------------------------------------- */

    observeBeats() {
      if (!('IntersectionObserver' in window)) return;

      if (!this.beatObserver) {
        this.beatObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(
              function (entry) {
                if (!entry.isIntersecting) return;
                this.activate(parseInt(entry.target.dataset.powerBeat, 10), false);
              }.bind(this)
            );
          }.bind(this),
          { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
        );
      }

      this.beats.forEach(
        function (beat) {
          this.beatObserver.observe(beat);
        }.bind(this)
      );
    }

    unobserveBeats() {
      if (this.beatObserver) this.beatObserver.disconnect();
    }

    activate(index, fromTab) {
      if (isNaN(index) || index === this.activeIndex) return;

      var previousBeat = this.beats[this.activeIndex];
      this.activeIndex = index;

      this.tabs.forEach(function (tab) {
        var isActive = parseInt(tab.dataset.powerTab, 10) === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.panels.forEach(function (panel) {
        panel.classList.toggle('is-active', parseInt(panel.dataset.powerPanel, 10) === index);
      });

      this.beats.forEach(function (beat) {
        beat.classList.toggle('is-active', parseInt(beat.dataset.powerBeat, 10) === index);
      });

      if (this.indicatorCurrent) this.indicatorCurrent.textContent = index + 1;

      if (this.progressFill && this.beats.length) {
        this.progressFill.style.transform = 'scaleX(' + (index + 1) / this.beats.length + ')';
      }

      // Un clic d'onglet ne recentre la page que si c'est bien le scroll qui
      // pilote ; en repli mobile le panneau se substitue sur place.
      if (fromTab && this.scrollDriven && this.beats[index]) {
        this.beats[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Pendant le scrub, position et cadrage viennent du scroll : les rejouer
      // ici ferait bégayer le mouvement.
      if (!this.scrubbing) this.applyKeyframe(this.keyframes[index], true);

      this.play(this.beats[index], previousBeat);
    }

    play(beat, previousBeat) {
      if (!beat) return;

      this.withViewer(
        function (viewer) {
          // On rembobine l'animation précédente au lieu de la couper : une
          // piste d'état comme « xray_on » laisserait sinon la montre
          // transparente dès qu'on remonte. timeScale négatif = marche
          // arrière, c'est l'idiome du SDK (onAnimationFinished renvoie le
          // signe pour cette raison).
          if (
            previousBeat &&
            previousBeat.dataset.animation &&
            previousBeat.dataset.animation !== beat.dataset.animation &&
            typeof viewer.playProductAnimation === 'function'
          ) {
            viewer.playProductAnimation(previousBeat.dataset.animation, true, false, -1);
          }

          if (beat.dataset.animation && typeof viewer.playProductAnimation === 'function') {
            viewer.playProductAnimation(
              beat.dataset.animation,
              true,
              beat.dataset.animationLoop === 'true'
            );
            this.currentAnimation = beat.dataset.animation;
          }

          if (this.xrayAnimation) {
            if (beat.dataset.animation === this.xrayAnimation) this.markXray(true);
            else if (previousBeat && previousBeat.dataset.animation === this.xrayAnimation) {
              this.markXray(false);
            }
          }

          if (typeof viewer.setObjectVisibility === 'function') {
            splitList(beat.dataset.objectHide).forEach(function (name) {
              viewer.setObjectVisibility(name, false);
            });

            splitList(beat.dataset.objectShow).forEach(function (name) {
              viewer.setObjectVisibility(name, true);
            });
          }
        }.bind(this)
      );
    }

    /* -- chorégraphie ---------------------------------------------------- */

    /**
     * Pose la montre à l'écran et cadre la caméra.
     * `animated` demande un vol de caméra (goToPos) plutôt qu'un saut ; le
     * déplacement à l'écran, lui, est transitionné en CSS.
     */
    applyKeyframe(frame, animated) {
      if (!frame) return;

      this.style.setProperty('--power-beat-x', String(frame.x));
      this.style.setProperty('--power-beat-y', String(frame.y));

      if (!frame.camera) return;

      this.withViewer(
        function (viewer) {
          this.applyCamera(viewer, frame.camera, animated);
        }.bind(this)
      );
    }

    applyCamera(viewer, camera, animated) {
      if (!camera) return;
      // L'événement de chargement peut avoir précédé notre écoute : l'état du
      // stage fait foi, l'événement ne sert qu'à réveiller la boucle.
      if (this.controlAt === null) {
        if (!this.stage || !this.stage.isLoaded) return;
        this.controlAt = Date.now() + this.takeoverDelay;
      }

      if (Date.now() < this.controlAt) return;

      // goToPos interpole depuis la position courante : il n'a pas besoin du
      // fondu, il EST le fondu. Seul setView, qui pose la caméra d'un coup,
      // a besoin qu'on parte de là où l'animation d'accueil l'a laissée.
      if (!animated) camera = this.blendTakeover(camera);

      // goToPos anime : le réaffirmer à chaque image le relancerait en boucle.
      // Seul setView, qui pose, peut être répété sans dommage.
      var asserting = !animated && this.assertUntil && Date.now() < this.assertUntil;

      if (!asserting && sameCamera(camera, this.appliedCamera)) return;
      this.appliedCamera = camera;

      var azimuth = camera.azimuth * DEG_TO_RAD;
      var polar = camera.polar * DEG_TO_RAD;
      var dolly = camera.dolly;

      if (this.desktopQuery && !this.desktopQuery.matches) dolly += this.mobileDolly;

      if (animated && typeof viewer.goToPos === 'function') {
        if (typeof viewer.setPanTarget === 'function') {
          viewer.setPanTarget(camera.targetX, camera.targetY, camera.targetZ);
        }

        viewer.goToPos({
          coordinates: {
            azimuth: azimuth,
            polar: polar,
            dolly: dolly
          },
          duration: this.cameraDuration,
          easing: 'power2.inOut',
          preventMovement: true
        });

        return;
      }

      if (typeof viewer.setView !== 'function') return;

      // Une seule fois, au tout premier cadrage : goToPos porte un drapeau
      // preventMovement, seul paramètre du SDK qui parle de couper le
      // pilotage caméra interne du viewer — celui de l'animation d'accueil.
      // setView, lui, pose la caméra sans rien empêcher, d'où l'accueil qui
      // reprenait la main juste après.
      if (!this.movementPrevented && typeof viewer.goToPos === 'function') {
        this.movementPrevented = true;

        viewer.goToPos({
          coordinates: { azimuth: azimuth, polar: polar, dolly: dolly },
          duration: 0.2,
          easing: 'power1.out',
          preventMovement: true
        });
      }

      viewer.setView(
        azimuth,
        polar,
        dolly,
        camera.targetX,
        camera.targetY,
        camera.targetZ
      );
    }

    /**
     * Reprise en main : au premier cadrage imposé, on part de la position où
     * le viewer a laissé sa caméra et on rejoint la nôtre en une seconde.
     * Sans ça, le passage de l'animation d'accueil à l'étape 1 est un saut.
     */
    blendTakeover(camera) {
      if (this.takeoverDone) return camera;

      if (this.takeoverStart === null) {
        this.takeoverStart = Date.now();
        this.takeoverFrom = orbitToCamera(this.stage && this.stage.lastOrbit);

        // Pas de relevé caméra disponible : rien à fondre, on prend la main.
        if (!this.takeoverFrom) {
          this.takeoverDone = true;
          return camera;
        }
      }

      var progress = (Date.now() - this.takeoverStart) / TAKEOVER_MS;

      if (progress >= 1) {
        this.takeoverDone = true;
        return camera;
      }

      return mixCameras(this.takeoverFrom, camera, easeInOut(progress));
    }

    startScrub() {
      if (this.scrubStarted) return;
      this.scrubStarted = true;

      // Le CSS transitionne les sauts d'étape ; pendant le scrub c'est la
      // boucle qui donne la position, une transition en plus traînerait.
      this.classList.add('is-scrubbing');

      // On démarre là où le scroll est déjà, sinon la montre traverse tout
      // l'écran au chargement d'une page ouverte au milieu de la section.
      this.smoothProgress = this.scrollProgress();

      this.onScrollFrame = this.runLoop.bind(this);
      window.addEventListener('scroll', this.onScrollFrame, { passive: true });
      window.addEventListener('resize', this.onScrollFrame);

      this.runLoop();
    }

    stopScrub() {
      if (!this.scrubStarted) return;
      this.scrubStarted = false;

      this.classList.remove('is-scrubbing');
      window.removeEventListener('scroll', this.onScrollFrame);
      window.removeEventListener('resize', this.onScrollFrame);

      if (this.frameRequest) {
        cancelAnimationFrame(this.frameRequest);
        this.frameRequest = null;
      }
    }

    runLoop() {
      if (!this.scrubStarted || this.frameRequest) return;

      this.frameRequest = requestAnimationFrame(
        function () {
          this.frameRequest = null;
          this.step();
        }.bind(this)
      );
    }

    /**
     * La caméra ne colle pas au scroll : elle le rattrape. Ce retard est ce
     * qui distingue un mouvement filmé d'un objet scotché à la molette — la
     * démo POWER (landing-page) fait exactement ça.
     * La boucle s'éteint dès que le rattrapage est fini ; le prochain cran de
     * scroll la rallume.
     */
    step() {
      if (!this.scrubStarted) return;

      var target = this.scrollProgress();
      var delta = target - this.smoothProgress;

      if (Math.abs(delta) < 0.0004) {
        this.smoothProgress = target;
        this.renderAt(this.smoothProgress);
        // Le scroll est stabilisé, mais la reprise de caméra, elle, court
        // encore : couper la boucle ici la figerait à mi-fondu. On exige un
        // viewer présent, sinon un viewer qui ne se charge jamais ferait
        // tourner la boucle indéfiniment.
        var asserting = this.assertUntil && Date.now() < this.assertUntil;

        if (this.hasCamera && this.viewer && (!this.takeoverDone || asserting)) {
          this.runLoop();
        }

        return;
      }

      this.smoothProgress += delta * this.smoothing;
      this.renderAt(this.smoothProgress);
      this.runLoop();
    }

    /**
     * Position continue dans l'histoire : 0 = première étape au centre de
     * l'écran, 1.5 = à mi-chemin entre la deuxième et la troisième.
     */
    scrollProgress() {
      var middle = window.innerHeight / 2;
      var centers = this.beats.map(function (beat) {
        var box = beat.getBoundingClientRect();
        return box.top + box.height / 2;
      });

      if (!centers.length) return 0;
      if (middle <= centers[0]) return 0;

      for (var i = 0; i < centers.length - 1; i++) {
        if (middle <= centers[i + 1]) {
          var span = centers[i + 1] - centers[i];
          return i + (span > 0 ? (middle - centers[i]) / span : 0);
        }
      }

      return centers.length - 1;
    }

    renderAt(progress) {
      if (!this.keyframes.length) return;

      var index = clamp(Math.floor(progress), 0, this.keyframes.length - 1);
      var next = Math.min(index + 1, this.keyframes.length - 1);
      // Le mouvement freine en arrivant sur une étape et repart en douceur :
      // une interpolation linéaire donne un travelling à vitesse constante,
      // qui ne « pose » jamais le plan.
      var t = easeInOut(progress - index);

      var from = this.keyframes[index];
      var to = this.keyframes[next];

      this.style.setProperty('--power-beat-x', lerp(from.x, to.x, t).toFixed(4));
      this.style.setProperty('--power-beat-y', lerp(from.y, to.y, t).toFixed(3));

      if (!this.hasCamera) return;

      // Le viewer n'est prêt qu'après le SDK : tant qu'il ne l'est pas, on se
      // contente de le réclamer une fois — réclamer à chaque frame de scroll
      // empilerait des dizaines de callbacks sur la même promesse.
      if (!this.viewer) {
        if (!this.viewerRequested) {
          this.viewerRequested = true;
          this.withViewer(this.runLoop.bind(this));
        }
        return;
      }

      this.applyCamera(this.viewer, mixCameras(from.camera, to.camera, t), false);
    }

    /**
     * Le modèle peut jouer une animation dès son chargement, réglée côté
     * POWER. On l'arrête et le modèle revient à sa pose de repos — sauf si
     * l'étape en cours déclenche la sienne : sa lecture a déjà coupé
     * l'accueil (le viewer ne joue qu'une piste à la fois), l'arrêter ici la
     * couperait aussitôt lancée.
     *
     * Surtout pas setProductAnimationPercent(…, 0) : il laisse la piste
     * active, en pause, et le viewer la réapplique à chaque image — les
     * aiguilles qu'elle anime ne répondent plus à la mise à l'heure. Pas non
     * plus playProductAnimation(…, false) : son deuxième paramètre est `hold`,
     * pas un interrupteur, et il relance l'accueil au lieu de l'arrêter.
     */
    freezeIntro() {
      if (this.dataset.freezeIntro !== 'true' || !this.stage) return;
      if (this.xrayOn) return;

      var active = this.beats[this.activeIndex || 0];
      if (active && active.dataset.animation) return;

      this.withViewer(
        function () {
          this.stage.releaseAnimations();
        }.bind(this)
      );
    }

    /**
     * L'accueil POWER peut démarrer avec un délai, après nos appels à
     * freezeIntro : on le rattrape à la fin de sa lecture. Une piste lancée
     * par le thème — celle de l'étape, la vue éclatée — reste retenue.
     */
    releaseForeignAnimation(data) {
      if (this.dataset.freezeIntro !== 'true' || !this.stage) return;
      if (!data || !data.animationName || data.timeScale < 0) return;

      // Le fondu caméra d'accueil signale aussi sa fin (PWRstartupCamera) :
      // seules les pistes du modèle nous concernent.
      if (sceneAnimationNames(this.stage.sceneInfo).indexOf(data.animationName) === -1) return;

      var active = this.beats[this.activeIndex || 0];
      if (active && data.animationName === active.dataset.animation) return;
      if (this.xrayOn && data.animationName === this.xrayAnimation) return;

      this.stage.releaseAnimations();
    }

    /* -- vue éclatée ----------------------------------------------------- */

    /**
     * Une seule piste, jouée en avant ou en arrière : c'est l'idiome du SDK
     * pour un état réversible (timeScale négatif), et c'est ce que fait la
     * démo POWER pour son mécanisme. L'état est partagé avec les étapes qui
     * déclenchent la même animation, sinon le libellé se désynchronise au
     * premier scroll.
     */
    setupXray() {
      this.xrayAnimation = this.dataset.xrayAnimation || '';
      // Un bouton flottant sur la scène, un sous chaque titre d'étape, ou les
      // deux : ils pilotent tous le même état.
      this.xrayButtons = Array.prototype.slice.call(
        this.querySelectorAll('[data-power-xray]')
      );
      this.xrayOn = false;

      if (!this.xrayButtons.length || !this.xrayAnimation) return;

      this.xrayButtons.forEach(
        function (button) {
          button.addEventListener(
            'click',
            function () {
              this.toggleXray();
            }.bind(this)
          );
        }.bind(this)
      );
    }

    toggleXray() {
      var target = !this.xrayOn;

      this.withViewer(
        function (viewer) {
          if (typeof viewer.playProductAnimation !== 'function') return;
          viewer.playProductAnimation(this.xrayAnimation, true, false, target ? 1 : -1);
          this.markXray(target);
        }.bind(this)
      );
    }

    // Met le bouton à l'état donné sans toucher à l'animation : sert quand
    // c'est le scroll, et non le clic, qui a changé la vue.
    markXray(state) {
      if (this.xrayOn === state || !this.xrayButtons) return;
      this.xrayOn = state;

      this.xrayButtons.forEach(function (button) {
        button.setAttribute('aria-pressed', state ? 'true' : 'false');
        button.classList.toggle('is-active', state);

        var label = button.querySelector('[data-power-xray-label]');
        if (!label) return;

        label.textContent = state ? button.dataset.labelHide : button.dataset.labelShow;
      });
    }

    /* -- relevé caméra (éditeur de thème) -------------------------------- */

    /**
     * Le SDK ne documente ni l'unité de l'azimut, ni l'échelle du dolly.
     * Dans l'éditeur, on affiche donc la position réelle de la caméra pendant
     * que le marchand fait tourner la montre à la souris : il lit les valeurs
     * à reporter dans l'étape, et un clic les copie.
     */
    setupHud() {
      this.hud = this.querySelector('[data-power-hud]');
      if (!this.hud) return;

      this.hud.addEventListener(
        'click',
        function () {
          if (!this.hudReading || !navigator.clipboard) return;

          navigator.clipboard.writeText(this.hudReading).then(
            function () {
              this.hud.classList.add('is-copied');
              setTimeout(
                function () {
                  this.hud.classList.remove('is-copied');
                }.bind(this),
                1200
              );
            }.bind(this)
          );
        }.bind(this)
      );

      this.withViewer(
        function (viewer) {
          if (typeof viewer.onOrbitUpdate !== 'function') return;

          viewer.onOrbitUpdate(
            function (data) {
              this.hudReading = formatOrbit(data);
              this.hud.textContent = this.hudReading;
            }.bind(this)
          );
        }.bind(this)
      );
    }
  }

  if (!customElements.get('power-story')) {
    customElements.define('power-story', PowerStory);
  }
})();
